import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { leaveService } from '../../services/leave.service';
import {
    FaPlus,
    FaCalendar,
    FaHourglass,
    FaCheck,
    FaTimes
} from 'react-icons/fa';

const MyLeaves = () => {
    const [leaves, setLeaves] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [stats, setStats] = useState({
        total: 0,
        pending: 0,
        approved: 0,
        rejected: 0
    });

    const [formData, setFormData] = useState({
        type: 'sick',
        startDate: '',
        endDate: '',
        reason: '',
        totalDays: 1
    });

    useEffect(() => {
        fetchLeaves();
    }, []);

    // calculate days
    useEffect(() => {
        if (formData.startDate && formData.endDate) {
            const start = new Date(formData.startDate);
            const end = new Date(formData.endDate);
            const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
            setFormData(prev => ({ ...prev, totalDays: diff }));
        }
    }, [formData.startDate, formData.endDate]);

    const fetchLeaves = async () => {
        try {
            setLoading(true);

            const res = await leaveService.getMyLeaves();

            // ✅ FIXED HERE
            const leavesData = res.data || [];

            setLeaves(leavesData);

            // stats
            setStats({
                total: leavesData.length,
                pending: leavesData.filter(l => l.status === 'pending').length,
                approved: leavesData.filter(l => l.status === 'approved').length,
                rejected: leavesData.filter(l => l.status === 'rejected').length
            });

        } catch (error) {
            toast.error('Failed to fetch leaves');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.startDate || !formData.endDate) {
            return toast.error('Select dates');
        }

        if (!formData.reason.trim()) {
            return toast.error('Enter reason');
        }

        try {
            setSubmitting(true);

            await leaveService.applyLeave({
                ...formData,
                days: formData.totalDays
            });

            toast.success('Leave applied successfully');
            setShowModal(false);
            fetchLeaves();

            setFormData({
                type: 'sick',
                startDate: '',
                endDate: '',
                reason: '',
                totalDays: 1
            });

        } catch {
            toast.error('Failed to apply');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusStyle = (status) => {
        switch (status) {
            case 'approved': return 'bg-green-100 text-green-600';
            case 'rejected': return 'bg-red-100 text-red-600';
            default: return 'bg-yellow-100 text-yellow-600';
        }
    };

    return (
        <div className="p-6">

            {/* HEADER */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold">My Leaves</h1>

                <button
                    onClick={() => setShowModal(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                >
                    <FaPlus /> Apply
                </button>
            </div>

            {/* STATS (NEW - MATCH ADMIN STYLE) */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <Stat title="Total" value={stats.total} icon={<FaCalendar />} />
                <Stat title="Pending" value={stats.pending} icon={<FaHourglass />} />
                <Stat title="Approved" value={stats.approved} icon={<FaCheck />} />
                <Stat title="Rejected" value={stats.rejected} icon={<FaTimes />} />
            </div>

            {/* TABLE */}
            <div className="bg-white rounded-lg shadow overflow-hidden">

                {loading ? (
                    <div className="text-center py-10">Loading...</div>
                ) : leaves.length === 0 ? (
                    <div className="text-center py-10 text-gray-500">
                        No leave applications found
                    </div>
                ) : (
                    <table className="min-w-full">
                        <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                            <tr>
                                <th className="px-6 py-3 text-left">Type</th>
                                <th className="px-6 py-3 text-left">Dates</th>
                                <th className="px-6 py-3 text-left">Days</th>
                                <th className="px-6 py-3 text-left">Status</th>
                                <th className="px-6 py-3 text-left">Reason</th>
                            </tr>
                        </thead>

                        <tbody className="divide-y">
                            {leaves.map(l => (
                                <tr key={l._id} className="hover:bg-gray-50">

                                    <td className="px-6 py-4 capitalize">{l.type}</td>

                                    <td className="px-6 py-4 text-sm">
                                        {new Date(l.startDate).toLocaleDateString()} -
                                        <br />
                                        {new Date(l.endDate).toLocaleDateString()}
                                    </td>

                                    <td className="px-6 py-4 font-semibold">
                                        {l.totalDays}
                                    </td>

                                    <td className="px-6 py-4">
                                        <span className={`px-3 py-1 rounded-full text-xs ${getStatusStyle(l.status)}`}>
                                            {l.status}
                                        </span>
                                    </td>

                                    <td className="px-6 py-4 text-sm max-w-xs truncate">
                                        {l.reason}
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* MODAL */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">

                    <div className="bg-white p-6 rounded-lg w-full max-w-md">

                        <h2 className="text-xl font-bold mb-4">Apply Leave</h2>

                        <form onSubmit={handleSubmit} className="space-y-3">

                            <select
                                value={formData.type}
                                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                                className="w-full border p-2 rounded"
                            >
                                <option value="sick">Sick</option>
                                <option value="casual">Casual</option>
                                <option value="paid">Paid</option>
                            </select>

                            <input
                                type="date"
                                value={formData.startDate}
                                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                                className="w-full border p-2 rounded"
                            />

                            <input
                                type="date"
                                value={formData.endDate}
                                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                                className="w-full border p-2 rounded"
                            />

                            <textarea
                                placeholder="Reason"
                                value={formData.reason}
                                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                                className="w-full border p-2 rounded"
                            />

                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="flex-1 bg-blue-600 text-white py-2 rounded"
                                >
                                    {submitting ? 'Submitting...' : 'Apply'}
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 bg-gray-300 py-2 rounded"
                                >
                                    Cancel
                                </button>
                            </div>

                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

const Stat = ({ title, value, icon }) => (
    <div className="bg-white p-6 rounded shadow flex justify-between">
        <div>
            <p className="text-sm text-gray-500">{title}</p>
            <p className="text-xl font-bold">{value}</p>
        </div>
        <div className="text-2xl text-blue-500">{icon}</div>
    </div>
);

export default MyLeaves;